import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { UsersThree, Plus, X, Trash, Crown } from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  fetchCommittees,
  fetchCommittee,
  createCommittee,
  updateCommittee,
  deleteCommittee,
  setCommitteeMember,
  removeCommitteeMember,
  COMMITTEE_ROLE_LABEL,
  type CommitteeListItem,
  type CommitteeRole,
} from '@/lib/committeesApi';
import { fetchUsers, type ApiUser } from '@/lib/usersApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

const ROLES: CommitteeRole[] = ['chair', 'secretary', 'member'];

export default function CommitteesPage() {
  const navigate = useNavigate();
  const [editing, setEditing] = useState<CommitteeListItem | 'new' | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['committees'], queryFn: fetchCommittees });
  const committees = q.data ?? [];

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Committees"
        subtitle="The chambers' governance bodies — management, pupillage, EDI — their remit and membership."
        actions={
          <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
            New committee
          </Button>
        }
      />

      {q.isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
      ) : committees.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-sm text-muted-foreground">No committees yet.</CardContent></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {committees.map((c) => (
            <Card key={c.id}>
              <CardContent className="cursor-pointer p-4" onClick={() => setOpenId(c.id)}>
                <div className="flex items-center gap-2">
                  <UsersThree className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                  <span className="font-medium text-foreground">{c.name}</span>
                  {!c.isActive && <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">Inactive</span>}
                </div>
                {c.remit && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{c.remit}</p>}
                <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{c.memberCount} member{c.memberCount === 1 ? '' : 's'}</span>
                  {c.chair && <span>· Chair: {c.chair.firstName} {c.chair.lastName}</span>}
                  {c.quorum != null && <span>· Quorum {c.quorum}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <CommitteeEditor
          committee={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { q.refetch(); setEditing(null); }}
        />
      )}
      {openId && (
        <CommitteeDrawer
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => q.refetch()}
          onEdit={() => { const c = committees.find((x) => x.id === openId); if (c) setEditing(c); setOpenId(null); }}
        />
      )}
    </div>
  );
}

function CommitteeEditor({ committee, onClose, onSaved }: { committee: CommitteeListItem | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(committee?.name ?? '');
  const [remit, setRemit] = useState(committee?.remit ?? '');
  const [quorum, setQuorum] = useState(committee?.quorum != null ? String(committee.quorum) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (name.trim().length < 2) { setError('Name must be at least 2 characters'); return; }
    setSaving(true); setError(null);
    try {
      const quorumNum = quorum.trim() ? Number(quorum) : undefined;
      if (committee) await updateCommittee(committee.id, { name: name.trim(), remit: remit.trim() || null, quorum: quorumNum ?? null });
      else await createCommittee({ name: name.trim(), remit: remit.trim() || undefined, quorum: quorumNum });
      toast.success(committee ? 'Committee saved' : 'Committee created');
      onSaved();
    } catch (err) { setError(getApiErrorMessage(err, 'Could not save')); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <ScrollLock />
      <div className="my-auto w-full max-w-md rounded-lg border border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-5 py-3 text-sm font-semibold text-foreground">{committee ? 'Edit committee' : 'New committee'}</div>
        <div className="space-y-4 px-5 py-4">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Management Committee" className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Remit (optional)</span>
            <textarea value={remit} onChange={(e) => setRemit(e.target.value)} rows={3} placeholder="What this committee is responsible for" className="w-full rounded-lg border border-border bg-card p-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Quorum (optional)</span>
            <input type="number" min={1} value={quorum} onChange={(e) => setQuorum(e.target.value)} placeholder="e.g. 3" className="h-9 w-32 rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </label>
          {error && <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">{error}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : committee ? 'Save' : 'Create'}</Button>
        </div>
      </div>
    </div>
  );
}

function CommitteeDrawer({ id, onClose, onChanged, onEdit }: { id: string; onClose: () => void; onChanged: () => void; onEdit: () => void }) {
  const qc = useQueryClient();
  const detailQ = useQuery({ queryKey: ['committee', id], queryFn: () => fetchCommittee(id) });
  const usersQ = useQuery({ queryKey: ['chambers-staff'], queryFn: () => fetchUsers(), staleTime: 60_000 });
  const [addUser, setAddUser] = useState('');
  const [addRole, setAddRole] = useState<CommitteeRole>('member');

  const refresh = () => { qc.invalidateQueries({ queryKey: ['committee', id] }); onChanged(); };

  const setMemberMut = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: CommitteeRole }) => setCommitteeMember(id, userId, role),
    onSuccess: refresh,
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not update member')),
  });
  const removeMut = useMutation({
    mutationFn: (userId: string) => removeCommitteeMember(id, userId),
    onSuccess: refresh,
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not remove member')),
  });
  const deleteMut = useMutation({
    mutationFn: () => deleteCommittee(id),
    onSuccess: () => { toast.success('Committee deleted'); onChanged(); onClose(); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not delete')),
  });

  const c = detailQ.data;
  const memberIds = useMemo(() => new Set((c?.members ?? []).map((m) => m.userId)), [c]);
  const staff = (usersQ.data ?? []).filter((u: ApiUser) => u.memberType !== 'solicitor' && !memberIds.has(u.id));

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <ScrollLock />
      <div className="h-full w-full max-w-md overflow-y-auto border-l border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2"><UsersThree className="h-4 w-4 text-brand-600 dark:text-brand-400" /><span className="text-sm font-semibold text-foreground">Committee</span></div>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        {!c ? (
          <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-5 p-5">
            <div>
              <div className="text-lg font-semibold text-foreground">{c.name}</div>
              {c.remit && <p className="mt-1 text-sm text-muted-foreground">{c.remit}</p>}
              <div className="mt-1 text-xs text-muted-foreground">{c.quorum != null ? `Quorum ${c.quorum}` : 'No quorum set'}</div>
              <div className="mt-2 flex gap-2">
                <Button variant="outline" size="sm" onClick={onEdit}>Edit</Button>
                <Button variant="outline" size="sm" className="text-red-600 dark:text-red-400" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>Delete</Button>
              </div>
            </div>

            <div>
              <div className="mb-2 text-xs font-medium text-muted-foreground">Members</div>
              {c.members.length === 0 ? (
                <div className="text-sm text-muted-foreground">No members yet.</div>
              ) : (
                <div className="space-y-1.5">
                  {c.members.map((m) => (
                    <div key={m.userId} className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-muted/40">
                      <div className="flex items-center gap-2 min-w-0">
                        {m.role === 'chair' && <Crown className="h-4 w-4 text-amber-500" />}
                        <span className="truncate text-sm text-foreground">{m.user.firstName} {m.user.lastName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={m.role}
                          onChange={(e) => setMemberMut.mutate({ userId: m.userId, role: e.target.value as CommitteeRole })}
                          className="h-7 rounded border border-border bg-card px-1.5 text-xs text-foreground focus:outline-none"
                        >
                          {ROLES.map((r) => <option key={r} value={r}>{COMMITTEE_ROLE_LABEL[r]}</option>)}
                        </select>
                        <button onClick={() => removeMut.mutate(m.userId)} className="rounded p-1 text-muted-foreground hover:bg-red-50 dark:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 dark:hover:bg-red-950/40"><Trash className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add member */}
            <div className="rounded-lg border border-border p-3">
              <div className="mb-2 text-xs font-medium text-muted-foreground">Add a member</div>
              <div className="flex items-center gap-2">
                <select value={addUser} onChange={(e) => setAddUser(e.target.value)} className="h-8 flex-1 rounded border border-border bg-card px-2 text-sm text-foreground focus:outline-none">
                  <option value="">Select staff…</option>
                  {staff.map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
                </select>
                <select value={addRole} onChange={(e) => setAddRole(e.target.value as CommitteeRole)} className="h-8 rounded border border-border bg-card px-2 text-sm text-foreground focus:outline-none">
                  {ROLES.map((r) => <option key={r} value={r}>{COMMITTEE_ROLE_LABEL[r]}</option>)}
                </select>
                <Button variant="primary" size="sm" disabled={!addUser || setMemberMut.isPending} onClick={() => { setMemberMut.mutate({ userId: addUser, role: addRole }); setAddUser(''); }}>Add</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
